import { Container, Typography, Box, Paper, Grid, Card, CardContent, Button, styled } from "@mui/material"
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement"
import MindfulnessIcon from "@mui/icons-material/Spa"
import AwarenessIcon from "@mui/icons-material/Visibility"

const styles = {
  container: {
    fontFamily: "Poppins, sans-serif",
    py: 6,
    mt:10
  },
  title: {
    fontSize: { xs: "2.5rem", md: "3.5rem" },
    fontWeight: 600,
    color: "#2C3E50",
    mb: 3,
    textAlign: "center",
    fontFamily:"poppins"
  },
  subtitle: {
    fontSize: { xs: "1.5rem", md: "2rem" },
    fontWeight: 600,
    color: "#34495E",
    mb: 4,
    textAlign: "center",
      fontFamily:"poppins"
  },
  paragraph: {
    fontSize: "1.1rem",
    color: "#5D6D7E",
    fontFamily:"poppins",
    mb: 3,
    lineHeight: 1.8,
    textAlign:"start"
  },
  paper: {
    p: 4,
    mb: 6,
    borderRadius: 4,
    boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
    transition: "transform 0.3s ease-in-out",
    "&:hover": {
      transform: "translateY(-8px)",
    },
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRadius: 4,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
    },
  },
  cardIcon: {
    fontSize: "3rem",
    color: "#3498DB",
    mb: 2,
  },
  cardTitle: {
    fontSize: "1.3rem",
    fontWeight: 600,
    fontFamily:"poppins",
    color: "#2C3E50",
    mb: 2,
  },
  ctaButton: {
    mt: 6,
    py: 1.5,
    px: 4,
    fontSize: "1.1rem",
    fontFamily:"poppins",
    borderRadius: 30,
    textTransform:"none",
                   backgroundColor: "#2E3B55",
    fontWeight: 500,
    boxShadow: "0 4px 14px rgba(52, 152, 219, 0.4)",
    "&:hover": {
      boxShadow: "0 6px 20px rgba(52, 152, 219, 0.6)",
    },
  },
}

const StyledQuote = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontStyle: "italic",
  color: "#7F8C8D",
  paddingLeft: theme.spacing(2),
  margin: theme.spacing(4, 0),
}))

const MeditationContent = () => {
  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Typography variant="h1" sx={styles.title}>
        Discovering Meditation
      </Typography>
      <Typography variant="h2" sx={styles.subtitle}>
        A Journey to Self-Awareness and Inner Peace
      </Typography>

      <Paper elevation={0} sx={styles.paper}>
        <Typography sx={styles.paragraph}>
          Meditation is the art of simply being. It's about cultivating awareness of the present moment and developing a
          deeper understanding of oneself. As you begin your meditation journey, you'll encounter thoughts and emotions
          that arise naturally. The key is not to engage with or analyze these thoughts, but to observe them without
          judgment as they come and go.
        </Typography>
        <StyledQuote>
          "Meditation is not about stopping thoughts, but recognizing that we are not our thoughts."
        </StyledQuote>
        <Typography sx={styles.paragraph}>
          As you delve deeper into your practice, you'll move from your conscious mind to your subconscious, where your
          true nature resides. This is where years of conditioning have shaped your thoughts, behaviors, and actions.
          Through consistent meditation, you'll gain insight into these patterns and learn how to reprogram them for
          optimal health and well-being.
        </Typography>
      </Paper>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Card sx={styles.card}>
            <CardContent>
              <SelfImprovementIcon sx={styles.cardIcon} />
              <Typography sx={styles.cardTitle}>Meditation vs. Concentration</Typography>
              <Typography sx={styles.paragraph}>
                Meditation is often confused with concentration, but they are distinct practices. Concentration involves
                focusing on a single object or thought, while meditation is about open awareness and observation of all
                experiences.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={styles.card}>
            <CardContent>
              <MindfulnessIcon sx={styles.cardIcon} />
              <Typography sx={styles.cardTitle}>The Essence of Dhyana</Typography>
              <Typography sx={styles.paragraph}>
                The root of meditation comes from the Sanskrit word "Dhyana," which means consciousness, awareness, and
                observation. It's about being fully present and at peace with the current moment.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={styles.card}>
            <CardContent>
              <AwarenessIcon sx={styles.cardIcon} />
              <Typography sx={styles.cardTitle}>Types of Awareness</Typography>
              <Typography sx={styles.paragraph}>
                Meditation cultivates different types of awareness, including passive and active awareness. It's about
                being conscious of your breath, heartbeat, surroundings, and internal experiences without getting caught
                up in them.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ textAlign: "center" }}>
        <Button variant="contained" color="primary" startIcon={<SelfImprovementIcon />} sx={styles.ctaButton}>
          Explore Our Meditation Program
        </Button>
      </Box>
    </Container>
  )
}

export default MeditationContent

